import React, { Component } from 'react';
import { DataSource } from '~/Api/DataSource';
import { SiMaxPrice } from '~/Api/User/ProductGroup/SiMaxPrice';
import { formatCountriesError } from '~/UI';
import CountryIcon from '~/UI/CountryIcon';
import Heading from '~/UI/Heading';

import styles from './styles.less';

interface Props {
	siMaxPrice: SiMaxPrice;
	dataSources: DataSource[][];
	formatPercentage(percentage: number): string;
}

export default class Calculation extends Component<Props> {
	renderEmptyResultReason() {
		const {
			siMaxPrice: { finalCalculation },
		} = this.props;
		const countryError = finalCalculation.findRequiresMaxOneEuropeanProductError();

		if (finalCalculation.hasMissingProductSettings()) {
			return <strong className={styles.rowError}>Some products settings are missing</strong>;
		}
		if (finalCalculation.requiresMinOneEuropeanProductErrorCode()) {
			return <strong className={styles.rowError}>Calculation requires at least one european country product</strong>;
		}
		if (countryError !== null) {
			return (
				<strong className={styles.rowError}>
					Calculation requires at most one product for each european country
					{formatCountriesError(countryError.duplicateCountries)}
				</strong>
			);
		}
		if (finalCalculation.isMissingReferenceSettings()) {
			return <strong className={styles.rowError}>Calculation requires reference settings</strong>;
		}
		if (finalCalculation.isIllegalProductPriceTypeSelected()) {
			return <strong className={styles.rowError}>Some products have illegal price selected</strong>;
		}
		return null;
	}

	render() {
		const {
			siMaxPrice: { finalCalculation },
			dataSources,
			formatPercentage,
		} = this.props;

		const referencedProduct =
			finalCalculation.referencedProduct !== null &&
			finalCalculation.referencedProduct.currentMaximumWholesalePrice !== null
				? finalCalculation.referencedProduct
				: null;

		return (
			<div className={styles.container}>
				<div className={styles.columns}>
					<div className={styles.leftColumn}>
						<Heading level={3}>Applicable reference prices:</Heading>
						{dataSources.map((dataSources) => (
							<div className={styles.row} key={dataSources[0].country.alpha2}>
								<span className={styles.countryIcon}>
									<CountryIcon code={dataSources[0].country.alpha2} name={dataSources[0].country.name} />
								</span>

								<span className={styles.countryDescription}>
									{dataSources.map((dataSource) => dataSource.shortName).join(', ')}
								</span>
							</div>
						))}
					</div>
					<div className={styles.centerColumn}>
						<Heading level={3}>Calculated max price:</Heading>
						<div className={styles.row}>
							<p className={styles.rowName}>Comparative price:</p>
							<p className={styles.rowValue}>
								{finalCalculation.comparativePrice !== null ? (
									<strong>{finalCalculation.comparativePrice.price.roundedHumanFormat()}</strong>
								) : null}
							</p>
						</div>
						<div className={styles.row}>
							<p className={styles.rowName}>Percentage of Comparative Price:</p>
							<p className={styles.rowValue}>
								{finalCalculation.percentsOfComparativePrice !== null ? (
									<strong>{formatPercentage(finalCalculation.percentsOfComparativePrice)}</strong>
								) : null}
							</p>
						</div>
						<div className={styles.row}>
							<p className={styles.rowName}>Max PEP value:</p>
							<p className={styles.rowValue}>
								{finalCalculation.maximumPepValue !== null ? (
									<strong>{finalCalculation.maximumPepValue.roundedHumanFormat()}</strong>
								) : null}
							</p>
						</div>
						<div className={styles.row}>
							<p className={styles.rowName}>Max wholesale value:</p>
							<p className={styles.rowValue}>
								{finalCalculation.maximumWholesalePrice !== null ? (
									<strong>{finalCalculation.maximumWholesalePrice.roundedHumanFormat()}</strong>
								) : null}
							</p>
						</div>
						{finalCalculation.emptyResultReason.length > 0 ? (
							<div className={styles.row}>
								<p>{this.renderEmptyResultReason()}</p>
							</div>
						) : null}
					</div>
					<div className={styles.rightColumn}>
						<Heading level={3}>&nbsp;</Heading>
						<div className={styles.row}>
							<p className={styles.smallRowName}>
								Current maximum wholesale price
								{referencedProduct !== null ? ` (${referencedProduct.dataSource.shortName})` : null}
							</p>
							<p className={styles.smallRowValue}>
								{referencedProduct !== null ? (
									<strong>{referencedProduct.currentMaximumWholesalePrice?.roundedHumanFormat()}</strong>
								) : null}
							</p>
						</div>
						<div className={styles.row}>
							<p className={styles.smallRowName}>Potential price drop:</p>
							<p className={styles.smallRowValue}>
								{finalCalculation.potentialPriceDropPercents !== null ? (
									<strong>{formatPercentage(finalCalculation.potentialPriceDropPercents)}</strong>
								) : null}
							</p>
						</div>
					</div>
				</div>
			</div>
		);
	}
}
